import { useEffect, useMemo, useRef, useState } from 'react';

import lock from 'assets/img/permissions/lock.svg';
import KEYBOARD_KEYS from 'constants/keyboard';
import eye from 'assets/img/permissions/eye.svg';
import connect from 'assets/img/permissions/connect.svg';
import {
  AuthorizeContextValue,
  useAuthorizeContext,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import LoadingSpinner from 'pages/Authorize/LoadingSpinner';
import { FINCH_BASE_URL } from 'constants/urls';
import styles from './Preamble.module.scss';
import { AvatarGroup, FinchAvatar, ClientAvatar } from 'components/Avatar';
import { AppLink } from 'components/Link/AppLink';
import { Separator, Stack } from '@chakra-ui/react';
import { AppShellButton } from 'components/Button/AppShellButton';
import { AppHeading } from 'components/Text/AppHeading';
import { useTrackConversionStep } from '../../../services/track-conversion';
import { useTrackConversionError } from 'services/track-conversion';
import { useRegisterStep } from 'services/register-step';
import {
  getClientDisplayName,
  getClientDisplayNameInSentence,
  isClientNamePlaceholder,
} from 'utils/connect-branding';
import {
  readLegalDocuments,
  titleForDocument,
  urlForDocument,
} from 'services/legal-documents';
import type { LegalDocument } from 'services/legal-documents';
import { LegalDocumentSheet } from 'components/LegalDocument/LegalDocumentSheet';

const Preamble = ({ nextStep, client }: AuthorizeContextValue) => {
  const authorizeContext = useAuthorizeContext();

  const forEmployersUrl = useMemo(() => {
    const url = new URL(`${FINCH_BASE_URL}/for-employers`);
    url.searchParams.set('utm_source', 'finch_connect');
    url.searchParams.set('utm_medium', 'product');
    url.searchParams.set('utm_campaign', 'for_employers');
    return url;
  }, []);

  const trackConversion = useTrackConversionStep();
  const trackConversionError = useTrackConversionError();
  const registerStep = useRegisterStep();
  const mounted = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      '[Preamble] effect fired, client:',
      !!client,
      'mounted:',
      mounted.current,
    );
    if (!mounted.current && client) {
      trackConversion({
        action: 'display',
      });
      // eslint-disable-next-line no-console
      console.log(
        '[Preamble] calling registerStep, sessionKey from context check...',
      );
      registerStep().catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[Preamble] registerStep failed:', err);
      });
      mounted.current = true;
    }
  }, [client]);

  const onContinue = () => {
    trackConversion({
      action: 'submit',
    });
    nextStep();
  };

  // Only documents we can render in-flow land here; the rest keep the new-tab
  // link.
  const [openDocument, setOpenDocument] = useState<LegalDocument | null>(null);

  // Keep ref up-to-date so the keyboard handler always calls the latest onContinue
  const onContinueRef = useRef(onContinue);
  useEffect(() => {
    onContinueRef.current = onContinue;
  });

  // The Enter handler below is bound to the document, so it fires while the
  // sheet is open too — advancing the flow out from under someone who is still
  // reading. In a ref for the same stale-closure reason as onContinue.
  const openDocumentRef = useRef<LegalDocument | null>(null);
  useEffect(() => {
    openDocumentRef.current = openDocument;
  });

  // Handle Enter key press — uses ref to avoid stale closure
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEYBOARD_KEYS.ENTER) {
        if (openDocumentRef.current) return;
        event.preventDefault();
        onContinueRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Compute displayName once, outside of JSX
  const displayName = getClientDisplayName(client);
  const displayNameInSentence = getClientDisplayNameInSentence(client);

  const legalDocuments = readLegalDocuments(client);

  return (
    <div className={styles.container}>
      {!client && <LoadingSpinner />}
      {client && (
        <>
          <div className={styles.content} data-testid="preamble-content">
            <Stack gap="6">
              <div className={styles.header}>
                <AvatarGroup>
                  {client.settings.connectBranding?.aliasEnabled && (
                    <ClientAvatar
                      logo={client.settings.connectBranding?.logo}
                      clientName={displayName}
                    />
                  )}
                  <FinchAvatar />
                </AvatarGroup>
                <AppHeading>
                  {displayName} uses Finch to connect your payroll and HR
                  systems.
                </AppHeading>
              </div>
              <Stack gap="6" align="center">
                <Separator />
                <div className={styles.bulletGroup}>
                  <div className={styles.iconGroup}>
                    <img src={lock} alt="" />
                  </div>
                  <div className={styles.textGroup}>
                    <div className={styles.textHeader}>Trusted & secure</div>
                    <div className={styles.subheader}>
                      Your information is handled securely with end-to-end
                      encryption. Your credentials are not shared with{' '}
                      {isClientNamePlaceholder(displayName)
                        ? 'anyone'
                        : displayName}
                      .
                    </div>
                  </div>
                </div>
                <div className={styles.bulletGroup}>
                  <div className={styles.iconGroup}>
                    <img src={eye} alt="" />
                  </div>
                  <div className={styles.textGroup}>
                    <div className={styles.textHeader}>Privacy first</div>
                    <div className={styles.subheader}>
                      You can review a list of permissions before granting{' '}
                      {displayNameInSentence} access to your system.
                    </div>
                  </div>
                </div>
                <div className={styles.bulletGroup}>
                  <div className={styles.iconGroup}>
                    <img src={connect} alt="" />
                  </div>
                  <div className={styles.textGroup}>
                    <div className={styles.textHeader}>Data portability</div>
                    <div className={styles.subheader}>
                      Finch does not sell your employer and employee data, and
                      will only use it with your permission.
                    </div>
                  </div>
                </div>
                <Separator />
                <AppLink
                  fontSize="xs"
                  variant="plain"
                  href={forEmployersUrl.toString()}
                  target="_blank"
                  rel="noopener noreferrer"
                  textAlign="center"
                >
                  Learn more about Finch
                </AppLink>
                {legalDocuments === undefined ? (
                  <div className={styles.privacyPrompt}>
                    By clicking &quot;Continue&quot; you agree to Finch&apos;s{' '}
                    <AppLink
                      variant="plain"
                      href={`${FINCH_BASE_URL}/legal/terms-of-service-for-end-users`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </AppLink>{' '}
                    and{' '}
                    <AppLink
                      variant="plain"
                      href={`${FINCH_BASE_URL}/legal/privacy`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      End User Privacy Policy
                    </AppLink>
                    .
                  </div>
                ) : (
                  legalDocuments.length > 0 && (
                    <div className={styles.privacyPrompt}>
                      By clicking &quot;Continue&quot; you agree to Finch&apos;s{' '}
                      {legalDocuments.map((document, index) => (
                        <span key={document.id}>
                          {index > 0 &&
                            (index === legalDocuments.length - 1
                              ? ' and '
                              : ', ')}
                          {/* Opens in-flow; the sheet falls back to the public
                            link if the catalog cannot serve the text. */}
                          <AppLink
                            variant="plain"
                            href={urlForDocument(document)}
                            onClick={(event) => {
                              event.preventDefault();
                              setOpenDocument(document);
                            }}
                            // Enter has to be handled here and stopped. The
                            // document-level handler below runs first and would
                            // advance the flow, because the sheet is not open
                            // yet so its guard does not apply — a keyboard user
                            // pressing Enter on the terms would skip past them.
                            onKeyDown={(event) => {
                              if (event.key !== KEYBOARD_KEYS.ENTER) return;
                              event.preventDefault();
                              event.stopPropagation();
                              setOpenDocument(document);
                            }}
                          >
                            {titleForDocument(document)}
                          </AppLink>
                        </span>
                      ))}
                      .
                    </div>
                  )
                )}
              </Stack>
            </Stack>

            <AppShellButton type="submit" onClick={onContinue} tabIndex={0}>
              Continue
            </AppShellButton>
          </div>

          {openDocument && (
            <LegalDocumentSheet
              document={openDocument}
              onClose={() => setOpenDocument(null)}
              // Same handler as out here, so acceptance keeps one tracked path.
              onContinue={() => {
                setOpenDocument(null);
                onContinue();
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default withAuthorizeContext(Preamble);
